// components/projects/NewProjectModal.js
// Type-aware create form. Types with a TYPE_UI entry in config/workflows.js get an intake section:
// priority, the custom fields listed in their `createFields`, and checkboxes for their pre-req tasks.
// Everything is driven by config + the type's field definitions, so adding a type needs no changes here.
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  getAvailableProjectTypes,
  getProjectTypeDisplay,
  getTypeUI,
  getPrereqTasks,
  deriveProjectName
} from '../../config/workflows.js';
import { useAlert } from '../common/AppAlert';

// Resolve a TYPE_UI createDefaults entry to an initial input value:
//   { daysFromToday: n } -> ISO date n days out;  { value: x } -> x as entered text.
const resolveDefault = (spec) => {
  if (!spec) return '';
  if (typeof spec.daysFromToday === 'number') {
    const d = new Date();
    d.setDate(d.getDate() + spec.daysFromToday);
    return d.toISOString().slice(0, 10);
  }
  if (spec.value !== undefined && spec.value !== null) return String(spec.value);
  return '';
};

// typeOptions (optional): restrict the Project Type dropdown to these types — the Thermal Workbench
// passes its two queue types so a request created there can only be TA or TM.
const NewProjectModal = ({ isOpen, onClose, onProjectCreate, user, onProjectCreated, api, defaultType, typeOptions }) => {
  const { alert } = useAlert();
  const [projectName, setProjectName] = useState('');
  const [projectType, setProjectType] = useState('');
  const [availableTypes, setAvailableTypes] = useState([]);

  // Intake fields for types with a TYPE_UI entry
  const [priority, setPriority] = useState('');
  const [fieldDefs, setFieldDefs] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [prereqChecked, setPrereqChecked] = useState({});
  // Derived names render locked (greyed) by default; the ✏️ button unlocks the input. Unlocking
  // alone doesn't stop auto-compose — only actually typing in the input does (nameEdited).
  const [nameUnlocked, setNameUnlocked] = useState(false);
  const [nameEdited, setNameEdited] = useState(false);

  const ui = getTypeUI(projectType);
  const prereqTasks = getPrereqTasks(projectType);
  const nameIsDerived = !!ui?.deriveName;

  useEffect(() => {
    if (isOpen) {
      const workflowTypes = getAvailableProjectTypes()
        .filter(t => !typeOptions || typeOptions.includes(t));
      setAvailableTypes(workflowTypes);
      if (defaultType && workflowTypes.includes(defaultType)) {
        setProjectType(defaultType);
      } else if (workflowTypes.length > 0) {
        setProjectType(workflowTypes[0]);
      }
      setProjectName('');
      setPriority('');
      setNameUnlocked(false);
      setNameEdited(false);
    }
  }, [isOpen, defaultType, typeOptions]);

  // Load the selected type's field definitions and seed the intake state. Definitions come from the
  // server so labels/types/order stay in one place; TYPE_UI.createFields decides which appear here.
  const loadTypeIntake = useCallback(async () => {
    const typeUI = getTypeUI(projectType);
    if (!isOpen || !typeUI) {
      setFieldDefs([]);
      setFieldValues({});
      setPrereqChecked({});
      return;
    }

    const initialChecked = {};
    getPrereqTasks(projectType).forEach(t => { initialChecked[t.id] = false; });
    setPrereqChecked(initialChecked);

    try {
      const types = await api.get('/project-types', user.token);
      const match = types.find(t => t.name === projectType);
      if (!match) { setFieldDefs([]); setFieldValues({}); return; }

      const defs = await api.get(`/project-types/${match.id}/fields`, user.token);
      const shown = defs.filter(d => typeUI.createFields.includes(d.field_key));
      setFieldDefs(shown);

      const initialValues = {};
      shown.forEach(d => {
        const options = typeUI.fieldOptions?.[d.field_key];
        initialValues[d.field_key] = options
          ? options[0]                                              // first option is the default
          : resolveDefault(typeUI.createDefaults?.[d.field_key]);
      });
      setFieldValues(initialValues);
    } catch (e) {
      console.error('Failed to load intake fields:', e);
      setFieldDefs([]);
      setFieldValues({});
    }
  }, [isOpen, projectType, api, user.token]);

  useEffect(() => { loadTypeIntake(); }, [loadTypeIntake]);

  // Single write path for intake fields so deriveName recomposes from the freshest values.
  const handleFieldChange = (key, value) => {
    const next = { ...fieldValues, [key]: value };
    setFieldValues(next);
    if (!nameEdited) {
      const derived = deriveProjectName(projectType, next);
      if (derived !== null) setProjectName(derived);
    }
  };

  const handleSubmit = async () => {
    if (!projectName.trim()) {
      alert('Please enter a project name');
      return;
    }

    if (projectName.includes(',')) {
      alert('Comma character (,) cannot be used - Please revise Project Name');
      return;
    }

    if (!projectType) {
      alert('Please select a project type');
      return;
    }

    const payload = {
      project_name: projectName,
      project_type: projectType
    };

    if (ui) {
      for (const key of ui.requiredCreateFields || []) {
        if (!String(fieldValues[key] ?? '').trim()) {
          const label = fieldDefs.find(d => d.field_key === key)?.field_label || key;
          alert(`${label} is required`);
          return;
        }
      }

      if (priority.trim()) payload.priority = priority.trim();

      const metadata = {};
      Object.entries(fieldValues).forEach(([key, value]) => {
        if (value !== null && value !== undefined && String(value).trim() !== '') {
          metadata[key] = String(value).trim();
        }
      });
      if (Object.keys(metadata).length > 0) payload.metadata = metadata;

      const prereqs_checked = Object.entries(prereqChecked)
        .filter(([, checked]) => checked)
        .map(([taskId]) => parseInt(taskId));
      if (prereqs_checked.length > 0) payload.prereqs_checked = prereqs_checked;
    }

    try {
      const newProject = await api.post('/projects', payload, user.token);
      // Remembered so the next Add New Project can default to it when no Type Filter is active.
      localStorage.setItem('gtrack_last_project_type', projectType);
      onProjectCreate(newProject);
      onClose();
      if (onProjectCreated) onProjectCreated(newProject);
    } catch (error) {
      alert('Error creating project: ' + error.message);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  const checkedCount = Object.values(prereqChecked).filter(Boolean).length;

  // Identity fields (TYPE_UI.headerFields) render as one row directly under Project Type; the rest
  // flow two per row below the divider — the create modal is 480px wide, so four across would
  // crush the inputs.
  const headerKeys = ui?.headerFields || [];
  const headerDefs = headerKeys.map(k => fieldDefs.find(d => d.field_key === k)).filter(Boolean);
  const bodyDefs = fieldDefs.filter(d => !headerKeys.includes(d.field_key));
  const fieldRows = [];
  for (let i = 0; i < bodyDefs.length; i += 2) fieldRows.push(bodyDefs.slice(i, i + 2));

  const renderFieldInput = (def) => {
    const options = ui?.fieldOptions?.[def.field_key];
    return options ? (
      <select
        className="select select-centered"
        value={fieldValues[def.field_key] || ''}
        onChange={(e) => handleFieldChange(def.field_key, e.target.value)}
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : (
      <input
        type={def.field_type === 'date' ? 'date' : 'text'}
        className="input input-centered"
        value={fieldValues[def.field_key] || ''}
        onChange={(e) => handleFieldChange(def.field_key, e.target.value)}
        onKeyDown={handleKeyDown}
      />
    );
  };

  // For deriveName types this renders just below the divider, locked (greyed) while it composes
  // live from the header fields, with an ✏️ unlock for manual edits; for everything else it sits
  // in its usual spot under Project Type.
  const nameField = (
    <div className="field field-center">
      <label htmlFor="projectName" className="field-label">Project Name</label>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
        <input
          id="projectName"
          type="text"
          className="input input-centered input-medium"
          placeholder={nameIsDerived ? 'Auto-fills from Part Number + Classification' : 'Enter project name'}
          value={projectName}
          onChange={(e) => { setNameEdited(true); setProjectName(e.target.value); }}
          onKeyDown={handleKeyDown}
          autoFocus={!nameIsDerived}
          disabled={nameIsDerived && !nameUnlocked}
        />
        {nameIsDerived && !nameUnlocked && (
          <button
            type="button"
            className="btn-icon"
            title="Edit name manually"
            onClick={() => setNameUnlocked(true)}
            style={{ fontSize: '16px' }}
          >
            ✏️
          </button>
        )}
      </div>
    </div>
  );

  return createPortal(
    <div className="modal-overlay">
      <div className="modal modal-small">
        <h3 className="modal-title-center">Add New Project</h3>

        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <div className="field-stack">
            <div className="field field-center">
              <label htmlFor="projectType" className="field-label">Project Type</label>
              <select
                id="projectType"
                className="select select-centered input-medium"
                value={projectType}
                onChange={(e) => setProjectType(e.target.value)}
              >
                {availableTypes.map(type => (
                  <option key={type} value={type}>{getProjectTypeDisplay(type)}</option>
                ))}
              </select>
            </div>

            {!nameIsDerived && nameField}

            {ui && (
              <>
                {headerDefs.length > 0 && (
                  <div className="ta-create-row">
                    {headerDefs.map(def => (
                      <div className="field field-center" style={{ flex: 1 }} key={def.field_key}>
                        <label className="field-label">{def.field_label}</label>
                        {renderFieldInput(def)}
                      </div>
                    ))}
                  </div>
                )}

                <div className="ta-create-divider">
                  <span>{getProjectTypeDisplay(projectType)}</span>
                </div>

                {nameIsDerived && nameField}

                <div className="field field-center">
                  <label className="field-label">Priority (optional)</label>
                  <input
                    type="text"
                    className="input input-centered"
                    style={{ width: '100%' }}
                    placeholder="Leave blank to append to end of queue"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value.slice(0, 3))}
                    onKeyDown={handleKeyDown}
                    maxLength={3}
                  />
                </div>

                {fieldRows.map((row, rowIndex) => (
                  <div className="ta-create-row" key={rowIndex}>
                    {row.map(def => (
                      <div className="field field-center" style={{ flex: 1 }} key={def.field_key}>
                        <label className="field-label">{def.field_label}</label>
                        {renderFieldInput(def)}
                      </div>
                    ))}
                  </div>
                ))}

                {prereqTasks.length > 0 && (
                  <div className="ta-create-prereqs">
                    <div className="ta-prereq-header">Pre-Reqs</div>
                    <div className="ta-prereq-hint">
                      Check those already satisfied. Project can be created with any/all unchecked — remaining items can be completed later from the project detail view.
                    </div>
                    {prereqTasks.map(t => (
                      <label key={t.id} className="ta-prereq-row">
                        <input
                          type="checkbox"
                          checked={!!prereqChecked[t.id]}
                          onChange={(e) => setPrereqChecked(prev => ({ ...prev, [t.id]: e.target.checked }))}
                        />
                        <span>{t.name}</span>
                      </label>
                    ))}
                    {checkedCount === prereqTasks.length && (
                      <div className="ta-prereq-note">
                        All pre-reqs checked — "All Pre-Reqs complete" will auto-fire at creation; the
                        "since pre-reqs satisfied" clock starts immediately.
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="btn-row" style={{ marginTop: '8px' }}>
              <button type="button" className="btn btn-create btn-flex" onClick={handleSubmit}>
                Okay - Create Project
              </button>
              <button type="button" className="btn btn-cancel btn-flex" onClick={onClose}>
                Exit Without Creating
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default NewProjectModal;
